<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import integrationAPI from 'dashboard/api/integrations';
import {
  eventDisplayName as displayNameForEvent,
  eventInventoryLabel as inventoryLabelForEvent,
  eventLineItemCount,
  eventMetadataSummary as metadataSummaryForEvent,
  eventProductImageAlt as productImageAltForEvent,
  eventProductImageUrl,
  eventProductUrl,
  eventValueLabel,
} from 'dashboard/components-next/Contacts/helpers/klaviyoEventMetadata';

/**
 * @typedef {Object} KlaviyoEvent
 * @property {number|string} id
 * @property {string|null} event_name
 * @property {string|null} metric_name
 * @property {string|null} occurred_at
 * @property {string|number|null} value
 * @property {Record<string, unknown>} metadata
 */

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['viewAll']);

const { t } = useI18n();
const crmProfilePayload = ref(null);
const isKlaviyoUnavailable = ref(false);
/** @type {import('vue').Ref<Set<string>>} */
const failedProductImageEventIds = ref(new Set());

/** @type {import('vue').ComputedRef<KlaviyoEvent[]>} */
const klaviyoEvents = computed(() => crmProfilePayload.value?.events || []);
const visibleEvents = computed(() => klaviyoEvents.value.slice(0, 3));
const hasAvailableKlaviyoIntegration = computed(
  () => crmProfilePayload.value?.integration_available !== false
);
const shouldRenderPreview = computed(
  () =>
    !isKlaviyoUnavailable.value &&
    hasAvailableKlaviyoIntegration.value &&
    klaviyoEvents.value.length > 0
);
const eventCountLabel = computed(() =>
  klaviyoEvents.value.length === 1
    ? t('CONTACT_PANEL.CRM.EVENT_SINGULAR')
    : t('CONTACT_PANEL.CRM.EVENT_PLURAL', {
        count: klaviyoEvents.value.length,
      })
);

/**
 * Checks whether the CRM profile endpoint reports Klaviyo as unavailable.
 *
 * @param {unknown} error API error raised by Axios.
 * @returns {boolean}
 */
const isKlaviyoUnavailableResponse = error =>
  error?.response?.data?.code === 'klaviyo_disabled_for_account';

/**
 * Returns the canonical display name for a parsed CRM event.
 *
 * @param {KlaviyoEvent} event Parsed CRM event payload.
 * @returns {string}
 */
const eventDisplayName = event =>
  displayNameForEvent(event, t('CONTACT_PANEL.CRM.EVENT'));

/**
 * Builds a compact commerce summary from typed event metadata.
 *
 * @param {KlaviyoEvent} event Parsed CRM event payload.
 * @returns {string}
 */
function eventMetadataSummary(event) {
  const lineItemCount = eventLineItemCount(event);
  return metadataSummaryForEvent(event, {
    itemCountLabel: lineItemCount
      ? t('CONTACT_PANEL.CRM.ITEM_COUNT', { count: lineItemCount })
      : null,
    inventoryLabel: inventoryLabelForEvent(event, {
      availableLabel: ({ count }) =>
        t('CONTACT_PANEL.CRM.INVENTORY_AVAILABLE', { count }),
      availableAtLocationLabel: ({ count, location }) =>
        t('CONTACT_PANEL.CRM.INVENTORY_AVAILABLE_AT_LOCATION', {
          count,
          location,
        }),
      availableAtLocationsLabel: ({ count, locationCount }) =>
        t('CONTACT_PANEL.CRM.INVENTORY_AVAILABLE_AT_LOCATIONS', {
          count,
          locationCount,
        }),
    }),
  });
}

const eventProductImageAlt = event =>
  productImageAltForEvent(event, t('CONTACT_PANEL.CRM.EVENT'));

/**
 * Builds a stable key for image-failure tracking.
 *
 * @param {KlaviyoEvent} event Parsed CRM event payload.
 * @returns {string}
 */
const imageFailureKey = event => String(event.id || event.occurred_at);

/**
 * Checks whether the product image already failed in this render session.
 *
 * @param {KlaviyoEvent} event Parsed CRM event payload.
 * @returns {boolean}
 */
const hasProductImageFailed = event =>
  failedProductImageEventIds.value.has(imageFailureKey(event));

/**
 * Hides a broken product image and keeps the event row readable.
 *
 * @param {KlaviyoEvent} event Parsed CRM event payload.
 * @returns {void}
 */
function markProductImageFailed(event) {
  failedProductImageEventIds.value = new Set([
    ...failedProductImageEventIds.value,
    imageFailureKey(event),
  ]);
}

/**
 * Loads the selected contact's parsed Klaviyo event timeline.
 *
 * @returns {Promise<void>}
 */
const loadKlaviyoEvents = async () => {
  if (!props.selectedContact?.id) return;

  isKlaviyoUnavailable.value = false;
  failedProductImageEventIds.value = new Set();
  try {
    const response = await integrationAPI.getKlaviyoContactProfile(
      props.selectedContact.id
    );
    crmProfilePayload.value = response.data;
  } catch (error) {
    crmProfilePayload.value = null;
    if (isKlaviyoUnavailableResponse(error)) {
      isKlaviyoUnavailable.value = true;
    }
  }
};

onMounted(loadKlaviyoEvents);
watch(() => props.selectedContact?.id, loadKlaviyoEvents);
</script>

<template>
  <div>
    <section v-if="shouldRenderPreview" class="mt-3 flex flex-col bg-n-solid-1">
      <div class="flex items-center justify-between gap-3 px-4 py-3">
        <div class="min-w-0">
          <h2 class="truncate text-sm font-semibold text-n-slate-12">
            {{ t('CONTACT_PANEL.CRM.EVENTS') }}
          </h2>
          <p class="text-xs text-n-slate-10">
            {{ eventCountLabel }}
          </p>
        </div>
        <button
          type="button"
          class="flex items-center gap-1.5 rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12"
          @click="emit('viewAll')"
        >
          <span class="i-lucide-list size-3.5" />
          {{ t('CONTACT_PANEL.CRM.VIEW_ALL') }}
        </button>
      </div>

      <div class="flex flex-col">
        <template
          v-for="(klaviyoEvent, index) in visibleEvents"
          :key="klaviyoEvent.id"
        >
          <div v-if="index > 0" class="mx-4 h-px bg-n-weak" />
          <div class="flex items-center justify-between gap-3 px-4 py-3">
            <div class="flex min-w-0 items-start gap-3">
              <div
                class="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-n-alpha-2 text-n-slate-10"
              >
                <img
                  v-if="
                    eventProductImageUrl(klaviyoEvent) &&
                    !hasProductImageFailed(klaviyoEvent)
                  "
                  data-testid="klaviyo-product-image"
                  class="size-full object-cover"
                  :src="eventProductImageUrl(klaviyoEvent)"
                  :alt="eventProductImageAlt(klaviyoEvent)"
                  loading="lazy"
                  @error="markProductImageFailed(klaviyoEvent)"
                />
                <span
                  v-else
                  data-testid="klaviyo-product-image-fallback"
                  class="i-lucide-image size-4"
                />
              </div>
              <div class="min-w-0">
                <a
                  v-if="eventProductUrl(klaviyoEvent)"
                  data-testid="klaviyo-product-link"
                  class="block truncate text-sm font-medium text-n-slate-12 hover:text-n-brand"
                  :href="eventProductUrl(klaviyoEvent)"
                  target="_blank"
                  rel="noopener noreferrer"
                  :aria-label="
                    t('CONTACT_PANEL.CRM.OPEN_PRODUCT', {
                      name: eventDisplayName(klaviyoEvent),
                    })
                  "
                >
                  {{ eventDisplayName(klaviyoEvent) }}
                </a>
                <p v-else class="truncate text-sm font-medium text-n-slate-12">
                  {{ eventDisplayName(klaviyoEvent) }}
                </p>
                <p
                  v-if="eventMetadataSummary(klaviyoEvent)"
                  class="truncate text-xs text-n-slate-11"
                >
                  {{ eventMetadataSummary(klaviyoEvent) }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{ klaviyoEvent.occurred_at }}
                </p>
              </div>
            </div>
            <span
              v-if="eventValueLabel(klaviyoEvent)"
              class="shrink-0 text-sm text-n-slate-11"
            >
              {{ eventValueLabel(klaviyoEvent) }}
            </span>
          </div>
        </template>
      </div>
    </section>
  </div>
</template>
